import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import Paginator from "./Paginator";
import { useStickyScrollbar } from "./useStickyScrollbar";

import type { Paginated } from "../../apis/types";

/**
 * Keep table sticky/frozen stacking local and below page chrome
 * (account panel headers ~z-20, page header z-30, notes z-40, modals z-50+, toasts z-[99999]).
 * Vertical scroll: non-frozen body < thead < frozen headers, so rows slide under the header row.
 * Horizontal scroll: thead < frozen body < frozen headers, so sliding columns stay under the
 * frozen gutter. Frozen body must sit above thead or Property/etc. paints through the expand column.
 */
const STICKY_THEAD_Z_CLASS = 'z-[10]';
const FROZEN_BODY_Z_BASE = 11;
const FROZEN_HEADER_Z_BASE = 16;
/**
 * Freeze-edge divider must be a box-shadow, not border-r.
 * Table cell borders paint in the table's border layer and scroll away / get covered
 * by the next column's opaque background. Shadow stays on the sticky element.
 */
const LAST_FROZEN_EDGE_CLASS =
  'shadow-[inset_-1px_0_0_0_#cbd5e1] dark:shadow-[inset_-1px_0_0_0_#475569]';

export type TableColumnSection = 'per-event' | 'running-balance' | 'ancillary';
export type TableColumnAlign = 'left' | 'right' | 'center';

export interface TableColumnConfig<Element> {
  label?: React.ReactElement | string | null;
  isHidden?: boolean;
  render: (value: Element) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  isSortable?: boolean;
  value?: string;
  contentClassName?: string;
  headerClassName?: string;
  cellClassName?: string;
  headerTitle?: string;
  align?: TableColumnAlign;
  isFrozen?: boolean;
  frozenMinWidth?: number;
  /** Fixed width (px) for non-frozen columns — sets min/width/max on header and cells. */
  columnWidth?: number;
  /** Minimum width (px) for columns that should grow (e.g. trailing text columns). */
  columnMinWidth?: number;
  dividerAfter?: boolean;
  columnSection?: TableColumnSection;
  /** When set, starts a column group header row spanning through the next groupLabel (or end). */
  groupLabel?: string;
  /** Optional custom renderer for the group header row (collapse toggles, etc.). */
  renderGroupLabel?: () => React.ReactNode;
  /**
   * When true, do not wrap this column's cell in the row `<Link>` from `getRowHref`.
   * Use for expand chevrons and other controls that must not navigate.
   */
  skipRowLink?: boolean;
  /**
   * Keep focus rings and menus that paint outside the cell.
   * Do not set this on frozen columns; visible overflow lets the scrollport leak.
   */
  overflowVisible?: boolean;
}

interface ColumnGroup {
  label: string;
  colspan: number;
  columnSection?: TableColumnSection;
  dividerAfter: boolean;
  startIndex: number;
  stickyLeft?: number;
  isFrozenGroup: boolean;
  renderGroupLabel?: () => React.ReactNode;
}

const alignClass = (align: TableColumnAlign | undefined, flex = false): string => {
  switch (align) {
    case 'right':
      return flex ? 'text-right justify-end' : 'text-right';
    case 'center':
      return flex ? 'text-center justify-center' : 'text-center';
    default:
      return flex ? 'text-left justify-start' : 'text-left';
  }
};

const SECTION_CELL_CLASSES: Record<TableColumnSection, string> = {
  'per-event': '',
  'running-balance': 'bg-slate-100/70 dark:bg-slate-900/50',
  ancillary: 'bg-slate-50 dark:bg-slate-900/30',
};

/** Opaque sticky header fill — must stay solid so frozen + non-frozen group headers match. */
const HEADER_BACKGROUND_CLASSES = 'bg-slate-50 dark:bg-slate-900';

function buildStickyLeftOffsets<Element>(colConfig: Array<TableColumnConfig<Element>>): Map<number, number> {
  const offsets = new Map<number, number>();
  let frozenLeft = 0;

  colConfig.forEach((col, index) => {
    if (col.isHidden ?? false) {
      return;
    }
    if (col.isFrozen) {
      offsets.set(index, frozenLeft);
      frozenLeft += col.frozenMinWidth ?? 96;
    }
  });

  return offsets;
}

function getLastFrozenColumnIndex<Element>(colConfig: Array<TableColumnConfig<Element>>): number {
  let lastIndex = -1;
  colConfig.forEach((col, index) => {
    if (!(col.isHidden ?? false) && col.isFrozen) {
      lastIndex = index;
    }
  });
  return lastIndex;
}

function getFrozenColumnStyle<Element>(
  col: TableColumnConfig<Element>,
  stickyLeft: number | undefined
): React.CSSProperties | undefined {
  if (stickyLeft == null) {
    return getColumnWidthStyle(col);
  }
  const width = col.frozenMinWidth ?? 96;
  return {
    left: stickyLeft,
    minWidth: width,
    width,
    maxWidth: width,
  };
}

function getColumnWidthStyle<Element>(col: TableColumnConfig<Element>): React.CSSProperties | undefined {
  if (col.columnWidth != null) {
    return { minWidth: col.columnWidth, width: col.columnWidth, maxWidth: col.columnWidth };
  }
  if (col.columnMinWidth != null) {
    return { minWidth: col.columnMinWidth, width: 'auto' };
  }
  return undefined;
}

function frozenCellBackground(striped: boolean | undefined, rowIndex: number): string {
  // Keep frozen body cells opaque (for horizontal scroll), but use the same base tones as
  // non-frozen rows so the frozen pane doesn't read as a darker slab under the group headers.
  return striped && rowIndex % 2 === 1 ? 'bg-slate-100 dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-900';
}

/** Match `tr` hover fill so opaque cell backgrounds (frozen / section / stripe) don't lag behind. */
const ROW_HOVER_CELL_BACKGROUND = 'group-hover:bg-slate-100 dark:group-hover:bg-white/[0.05]';
/** Frozen panes stay opaque on hover so sliding columns cannot show through. */
const FROZEN_ROW_HOVER_CELL_BACKGROUND = 'group-hover:bg-slate-100 dark:group-hover:bg-slate-800';

/**
 * Row chrome is copied onto cells so frozen panes match. Left accent borders must stay on the
 * first visible cell only — otherwise every column paints a thick border.
 */
function splitRowChrome(rowClassName?: string): { cellChrome: string; edgeChrome: string } {
  if (rowClassName == null || rowClassName === '') {
    return { cellChrome: '', edgeChrome: '' }
  }
  const edge: string[] = []
  const rest: string[] = []
  for (const token of rowClassName.split(/\s+/).filter(Boolean)) {
    if (token.includes('border-l')) edge.push(token)
    else rest.push(token)
  }
  return { cellChrome: rest.join(' '), edgeChrome: edge.join(' ') }
}

function bodyCellBackground(args: {
  striped: boolean | undefined;
  rowIndex: number;
  isFrozen: boolean;
  columnSection: TableColumnSection | undefined;
  interactive: boolean;
  /** Row-level chrome (nested highlight, opacity). Applied on every cell so frozen panes match. */
  rowClassName?: string;
}): string {
  const { striped, rowIndex, isFrozen, columnSection, interactive, rowClassName: rowChrome } = args;
  const hover = interactive
    ? isFrozen
      ? FROZEN_ROW_HOVER_CELL_BACKGROUND
      : ROW_HOVER_CELL_BACKGROUND
    : undefined;

  if (isFrozen) {
    return classnames(frozenCellBackground(striped, rowIndex), hover, rowChrome);
  }
  if (columnSection != null) {
    return classnames(SECTION_CELL_CLASSES[columnSection], hover, rowChrome);
  }
  return classnames(
    striped && rowIndex % 2 === 1
      ? 'bg-slate-100/80 dark:bg-slate-900/40'
      : 'bg-slate-50 dark:bg-slate-900/50',
    hover,
    rowChrome
  );
}

function buildColumnGroups<Element>(
  colConfig: Array<TableColumnConfig<Element>>,
  stickyLeftOffsets: Map<number, number>
): ColumnGroup[] {
  const groups: ColumnGroup[] = [];
  let current: ColumnGroup | null = null;

  colConfig.forEach((col, index) => {
    if (col.isHidden ?? false) {
      return;
    }

    if (col.groupLabel != null) {
      if (current != null) {
        groups.push(current);
      }
      current = {
        label: col.groupLabel,
        colspan: 1,
        columnSection: col.columnSection,
        dividerAfter: col.dividerAfter ?? false,
        startIndex: index,
        stickyLeft: stickyLeftOffsets.get(index),
        isFrozenGroup: col.isFrozen === true,
        renderGroupLabel: col.renderGroupLabel,
      };
      return;
    }

    if (current != null) {
      current.colspan += 1;
      if (col.dividerAfter) {
        current.dividerAfter = true;
      }
      if (col.columnSection != null && current.columnSection == null) {
        current.columnSection = col.columnSection;
      }
      if (col.isFrozen) {
        current.isFrozenGroup = true;
      }
      return;
    }

    current = {
      label: '',
      colspan: 1,
      columnSection: col.columnSection,
      dividerAfter: col.dividerAfter ?? false,
      startIndex: index,
      stickyLeft: stickyLeftOffsets.get(index),
      isFrozenGroup: col.isFrozen === true,
    };
  });

  if (current != null) {
    groups.push(current);
  }

  return groups;
}

/** Default collapsed preview — headers + a few rows; overflow is clipped (no nested Y-scroll). */
const DEFAULT_COLLAPSED_HEIGHT_CLASS = 'max-h-56';

/** Options for Table-owned horizontal scroll with an always-visible bottom track. */
export type TableScrollShell =
  | boolean
  | {
      /** Max-height classes on the scrollport (e.g. `max-h-64 …`). Shell shrink-wraps content up to this cap. */
      heightClassName?: string;
      /** Rounded bordered container. Defaults to true. */
      bordered?: boolean;
      /**
       * Collapsed by default: clip at `collapsedHeightClassName` with no nested Y-scroll.
       * Expand applies `heightClassName` + overflow-y-auto for a sticky workspace.
       */
      expandable?: boolean;
      /** Max-height while collapsed (overflow clipped). Defaults to `max-h-56`. */
      collapsedHeightClassName?: string;
      /** Start expanded (scrollable). Defaults to false. */
      defaultExpanded?: boolean;
    };

export interface TableProps<Element extends { id: string }> {
  colConfig: Array<TableColumnConfig<Element>>;
  data: Paginated<Element>;
  isLoading?: boolean;
  isSelected?: (value: Element) => boolean;
  hidePaginator?: boolean;
  compact?: boolean;
  handleClick?: (value: Element) => void;
  /**
   * If provided, each row's cells are wrapped in a react-router `<Link>` with this href.
   * This lets the browser treat the row as a real navigation target — middle-click to open
   * in a new tab, right-click for the context menu, cmd/ctrl-click, etc. Return `undefined`
   * to opt a specific row out of link behavior. Takes precedence over `handleClick` when
   * both are set.
   */
  getRowHref?: (value: Element) => string | undefined;
  handlePaginatorClick?: (page: number) => void;
  onSort?: (columnLabel: string, sortDirection: string) => void;
  sortingStatus?: Map<string, string | null>;
  hideHeaders?: boolean;
  /**
   * Skip Table's default overflow-x wrapper. Prefer `scrollShell` for dense tables —
   * that mode owns scrolling and implies noOverflow.
   */
  noOverflow?: boolean;
  striped?: boolean;
  stickyHeader?: boolean;
  showColumnGroups?: boolean;
  /**
   * Tighter padding on frozen columns. Defaults to true when `compact` and any column is frozen.
   */
  compactFrozen?: boolean;
  rowClassName?: (record: Element, index: number, allRecords: Element[]) => string;
  /** Row hover highlight without row navigation (e.g. tables with inline links). */
  highlightRowsOnHover?: boolean;
  /** Override default `min-w-full` on the `<table>` element (e.g. `w-max` for content-sized tables). */
  tableClassName?: string;
  /**
   * Own horizontal scroll + always-visible bottom track (shared shell used by billing/invoices).
   * Pass `true` for a bordered content-height shell, or an object for height/border options.
   */
  scrollShell?: TableScrollShell;
}

function resolveScrollShell(scrollShell: TableScrollShell | undefined): {
  enabled: boolean;
  heightClassName?: string;
  bordered: boolean;
  expandable: boolean;
  collapsedHeightClassName: string;
  defaultExpanded: boolean;
} {
  if (scrollShell == null || scrollShell === false) {
    return {
      enabled: false,
      bordered: false,
      expandable: false,
      collapsedHeightClassName: DEFAULT_COLLAPSED_HEIGHT_CLASS,
      defaultExpanded: false,
    };
  }
  if (scrollShell === true) {
    return {
      enabled: true,
      bordered: true,
      expandable: false,
      collapsedHeightClassName: DEFAULT_COLLAPSED_HEIGHT_CLASS,
      defaultExpanded: false,
    };
  }
  return {
    enabled: true,
    heightClassName: scrollShell.heightClassName,
    bordered: scrollShell.bordered !== false,
    expandable: scrollShell.expandable === true,
    collapsedHeightClassName: scrollShell.collapsedHeightClassName ?? DEFAULT_COLLAPSED_HEIGHT_CLASS,
    defaultExpanded: scrollShell.defaultExpanded === true,
  };
}

export function Table<Element extends { id: string }>(
  props: TableProps<Element>
): React.ReactElement | null {
  const {
    colConfig,
    data: { results = [], ...rest },
    isLoading,
    handleClick,
    getRowHref,
    hidePaginator,
    onSort,
    sortingStatus,
    hideHeaders,
    noOverflow,
    compact,
    striped,
    stickyHeader,
    showColumnGroups,
    compactFrozen,
    rowClassName,
    highlightRowsOnHover,
    tableClassName,
    scrollShell,
  } = props

  const shell = resolveScrollShell(scrollShell);
  const scrollRef = useRef<HTMLDivElement>(null);
  const recs = results;
  const [shellExpanded, setShellExpanded] = useState(shell.defaultExpanded);
  const [shellOverflows, setShellOverflows] = useState(false);
  // Rebind sticky scrollbar when the shell nodes actually exist (after loading/empty).
  const scrollShellBound =
    shell.enabled && !(isLoading ?? false) && (recs?.length ?? 0) > 0;
  const { stickyRef: stickyScrollRef, scrollWidth, hasHorizontalOverflow } = useStickyScrollbar(
    scrollRef,
    scrollShellBound
  );

  useEffect(() => {
    setShellExpanded(shell.defaultExpanded);
  }, [shell.defaultExpanded]);

  useEffect(() => {
    if (!shell.enabled || !shell.expandable || !scrollShellBound) {
      setShellOverflows(false);
      return;
    }
    const node = scrollRef.current;
    if (node == null) {
      return;
    }

    const measure = () => {
      if (shellExpanded) {
        // Keep the collapse control visible after expanding; remeasure when collapsing.
        return;
      }
      setShellOverflows(node.scrollHeight > node.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    shell.enabled,
    shell.expandable,
    scrollShellBound,
    shellExpanded,
    recs?.length,
    colConfig,
  ]);

  const stickyLeftOffsets = buildStickyLeftOffsets(colConfig);
  const lastFrozenColumnIndex = getLastFrozenColumnIndex(colConfig);
  const hasFrozenColumns = lastFrozenColumnIndex >= 0;
  const useCompactFrozen = compactFrozen ?? (compact === true && hasFrozenColumns);
  const totalFrozenWidth = colConfig.reduce((sum, col) => {
    if ((col.isHidden ?? false) || col.isFrozen !== true) {
      return sum;
    }
    return sum + (col.frozenMinWidth ?? 96);
  }, 0);
  const columnGroups = showColumnGroups === true ? buildColumnGroups(colConfig, stickyLeftOffsets) : [];
  const hasColumnGroups = columnGroups.some((group) => group.label !== '' || group.renderGroupLabel != null);
  const headerPaddingClasses = compact ? 'px-2 py-1' : 'px-4 py-2';
  const cellPaddingClasses = compact ? 'px-2 py-1' : 'p-4';
  const frozenHeaderPaddingClasses = useCompactFrozen ? 'px-1 py-0.5' : headerPaddingClasses;
  const frozenCellPaddingClasses = useCompactFrozen ? 'px-1 py-0.5' : cellPaddingClasses;
  const stickyGroupHeaderClass = stickyHeader ? `sticky top-0 ${STICKY_THEAD_Z_CLASS} h-7 align-middle` : '';
  const stickyColumnHeaderClass = stickyHeader
    ? hasColumnGroups
      ? `sticky top-7 ${STICKY_THEAD_Z_CLASS}`
      : `sticky top-0 ${STICKY_THEAD_Z_CLASS}`
    : '';
  const headerTypographyClasses = compact
    ? 'text-xs font-medium normal-case text-slate-500 dark:text-slate-400'
    : 'text-xs font-medium uppercase tracking-wider dark:text-white';

  const isTableLoading = isLoading ?? false

  if (!isTableLoading && recs?.length === 0) {
    return <div className="text-sm font-medium dark:text-white">No results found</div>
  }

  const loadingOverlay = isTableLoading ? (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-slate-900/60"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
        <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        Loading...
      </div>
    </div>
  ) : null

  if (isTableLoading && recs.length === 0) {
    return (
      <div
        className={classnames(
          'relative min-h-[16rem]',
          shell.enabled && shell.bordered && 'rounded-md border border-slate-200 dark:border-slate-700'
        )}
      >
        {loadingOverlay}
      </div>
    )
  }

  const headers = colConfig.map((col, index) => {
    if (col.isHidden ?? false) {
      return null
    }

    // If a column is sortable and the sorting status is defined, set the sorting status of the column
    // Otherwise set default values
    let columnSortingStatus: string | undefined | null = 'NONE'

    // Cycle through the sorting status of each column based on its current status
    const handleColumnHeaderClick = (valueToSort: string | undefined): void => {
      if (onSort !== undefined && sortingStatus !== undefined) {
        columnSortingStatus = sortingStatus.get(valueToSort ?? '')

        if (columnSortingStatus === 'ASC') {
          onSort(valueToSort ?? '', 'DESC')
        } else if (columnSortingStatus === 'DESC') {
          onSort(valueToSort ?? '', 'NONE')
        } else {
          onSort(valueToSort ?? '', 'ASC')
        }
      }
    }

    // Handle parsing for first column which has two headers
    const headerLabelsList = col.label?.toString().split('/') ?? []
    const headerValuesList = col.value?.toString().split('/') ?? []

    const stickyLeft = stickyLeftOffsets.get(index);
    const isLastFrozen = index === lastFrozenColumnIndex;

    return (
      <th
        key={index}
        scope="col"
        title={col.headerTitle}
        style={{
          ...getFrozenColumnStyle(col, stickyLeft),
          ...(col.isFrozen ? { zIndex: FROZEN_HEADER_Z_BASE + index } : {}),
        }}
        className={classnames(
          col.isFrozen ? frozenHeaderPaddingClasses : headerPaddingClasses,
          headerTypographyClasses,
          alignClass(col.align),
          col.headerClassName,
          stickyColumnHeaderClass,
          HEADER_BACKGROUND_CLASSES,
          col.dividerAfter && !isLastFrozen && 'border-r border-slate-300 dark:border-slate-600',
          col.isFrozen && 'sticky',
          stickyHeader && 'overflow-hidden isolate',
          isLastFrozen && LAST_FROZEN_EDGE_CLASS
        )}
      >
        {/* If a column is sortable render the appropriate chevron icon based on its sorting status */}
        {onSort != null && sortingStatus != null && col.isSortable === true ? (
          <>
            <button onClick={() => handleColumnHeaderClick(headerValuesList[0])} className="flex items-center">
              <span className="text-left text-xs font-medium uppercase tracking-wider mr-2">{headerLabelsList[0] ?? null}</span>
              {sortingStatus.get(headerValuesList[0] ?? '') === 'ASC' && (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path
                    fillRule="evenodd"
                    d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {sortingStatus.get(headerValuesList[0] ?? '') === 'DESC' && (
                // Chevron down icon from Heroicon's mini icons set
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
            {/* Special case for first column of FileExplorer table which stores both a name and directory */}
            {headerLabelsList.length >= 2 && headerValuesList.length >= 2 && (
              <button onClick={() => handleColumnHeaderClick(headerValuesList[1])} className="flex items-center">
                <span className="text-left text-xs font-medium uppercase tracking-wider mr-2">{headerLabelsList[1] ?? null}</span>
                {sortingStatus.get(headerValuesList[1] ?? '') === 'ASC' && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path
                      fillRule="evenodd"
                      d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {sortingStatus.get(headerValuesList[1] ?? '') === 'DESC' && (
                  // Chevron down icon from Heroicon's mini icons set
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            )}
          </>
        ) : (
          <>
            {col.label ?? null}
            {col.renderHeader?.() ?? null}
          </>
        )}
      </th>
    )
  })

  const groupHeaders =
    hasColumnGroups &&
    (() => {
      let lastFrozenGroupIndex = -1;
      columnGroups.forEach((group, index) => {
        if (group.isFrozenGroup) {
          lastFrozenGroupIndex = index;
        }
      });
      return columnGroups.map((group, groupIndex) => {
        const isLastFrozenGroup = group.isFrozenGroup && groupIndex === lastFrozenGroupIndex;
        return (
        <th
          key={`group-${groupIndex}`}
          colSpan={group.colspan}
          scope="colgroup"
          style={
            group.stickyLeft != null
              ? {
                  left: group.stickyLeft,
                  ...(group.isFrozenGroup ? { minWidth: totalFrozenWidth, width: totalFrozenWidth, maxWidth: totalFrozenWidth, zIndex: FROZEN_HEADER_Z_BASE + group.startIndex } : {}),
                }
              : undefined
          }
          className={classnames(
            headerPaddingClasses,
            stickyGroupHeaderClass,
            'text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500',
            HEADER_BACKGROUND_CLASSES,
            group.dividerAfter && !isLastFrozenGroup && 'border-r border-slate-300 dark:border-slate-600',
            group.isFrozenGroup && 'sticky',
            isLastFrozenGroup && LAST_FROZEN_EDGE_CLASS
          )}
        >
          {group.renderGroupLabel?.() ?? group.label}
        </th>
        );
      });
    })();

  const records =
    recs?.length > 0 &&
    recs.map((record, recIndex) => {
      const href = getRowHref?.(record)
      const isInteractive = !!href || !!handleClick || highlightRowsOnHover === true
      const rowExtraClassName = rowClassName?.(record, recIndex, recs)
      const { cellChrome, edgeChrome } = splitRowChrome(rowExtraClassName)
      const firstVisibleColIndex = colConfig.findIndex((c) => !(c.isHidden ?? false))
      return (
        <tr
          key={record.id}
          className={classnames(
            'group',
            striped && recIndex % 2 === 1
              ? 'bg-slate-100/80 dark:bg-slate-900/40'
              : 'bg-slate-50 dark:bg-slate-950/50',
            isInteractive && 'hover:bg-slate-100 dark:hover:bg-white/[0.05] transition-colors',
            rowExtraClassName
          )}
        >
          {colConfig.map((col, colIndex) => {
            if (col.isHidden ?? false) {
              return null
            }
            const stickyLeft = stickyLeftOffsets.get(colIndex);
            const isLastFrozen = colIndex === lastFrozenColumnIndex;
            const cellBackground = bodyCellBackground({
              striped,
              rowIndex: recIndex,
              isFrozen: col.isFrozen === true,
              columnSection: col.columnSection,
              interactive: isInteractive,
              rowClassName: classnames(
                cellChrome,
                colIndex === firstVisibleColIndex && edgeChrome
              ),
            });
            // Visual chrome (bg, sticky, borders) stays on <td> so row-height cells paint evenly.
            // Padding/align live on the inner <Link> when present so the full cell is clickable.
            const tdChromeClassName = classnames(
              'text-sm font-base',
              cellBackground,
              col.dividerAfter && !isLastFrozen && 'border-r border-slate-300 dark:border-slate-600',
              col.isFrozen ? 'sticky isolate' : 'z-0',
              isLastFrozen && LAST_FROZEN_EDGE_CLASS,
              { 'cursor-pointer': handleClick && href == null }
            );
            const tdStyle = {
              ...getFrozenColumnStyle(col, stickyLeft),
              ...(col.isFrozen ? { zIndex: FROZEN_BODY_Z_BASE + colIndex } : {}),
            };
            const cellOverflowVisible = col.overflowVisible === true
            const tdOverflowVisible = cellOverflowVisible
            const content = (
              <div
                className={classnames(
                  'relative z-[1] min-w-0',
                  cellOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
                  col.contentClassName
                )}
              >
                {col.render(record)}
              </div>
            )
            const cellPaddingClassesForCol = col.isFrozen ? frozenCellPaddingClasses : cellPaddingClasses

            // When a row href is provided, move the padding onto the <Link> so the whole cell area
            // (including what was the <td>'s padding) becomes clickable as a real link. This gives
            // middle-click, right-click, and cmd/ctrl-click native browser behavior.
            // skipRowLink keeps expand controls / buttons out of the navigation target.
            if (href && col.skipRowLink !== true) {
              return (
                <td
                  className={classnames(
                    // Do not use h-px here: Firefox treats it as a definite 1px cell height, so
                    // border-box padding on the inner Link collapses. Size the cell from the Link.
                    // `after:inset-0` on the Link stretches the hit target to this cell: table
                    // columns are wider than a chip or a blank count, and `w-full` on the
                    // <a> still shrinks to content.
                    'relative min-w-0 p-0',
                    tdOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
                    tdChromeClassName,
                    col.cellClassName
                  )}
                  key={`${record.id}-${colIndex}`}
                  style={tdStyle}
                >
                  <Link
                    to={href}
                    className={classnames(
                      cellPaddingClassesForCol,
                      'flex w-full min-w-0 items-center whitespace-nowrap',
                      alignClass(col.align, true),
                      "cursor-pointer after:absolute after:inset-0 after:z-0 after:content-['']"
                    )}
                    // Neutralize the global `a` / `a:hover` rules in src/index.css so the
                    // row text doesn't pick up a link color, weight, or underline — only
                    // the row background changes on hover.
                    style={{ color: 'inherit', fontWeight: 'inherit', textDecoration: 'inherit' }}
                  >
                    {content}
                  </Link>
                </td>
              )
            }

            return (
              <td
                className={classnames(
                  cellPaddingClassesForCol,
                  'min-w-0 whitespace-nowrap',
                  tdOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
                  alignClass(col.align),
                  col.cellClassName,
                  tdChromeClassName
                )}
                style={tdStyle}
                key={`${record.id}-${colIndex}`}
                onClick={() => (col.skipRowLink === true ? undefined : handleClick?.(record))}
              >
                {content}
              </td>
            )
          })}
        </tr>
      )
    })

  const tableContent = (
    <table className={classnames('divide-y divide-slate-200 dark:divide-slate-700', tableClassName ?? 'min-w-full')}>
      {hideHeaders !== true && (
        <thead className={HEADER_BACKGROUND_CLASSES}>
          {groupHeaders && <tr>{groupHeaders}</tr>}
          <tr>{headers}</tr>
        </thead>
      )}
      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">{records}</tbody>
    </table>
  )

  const scrollTrack =
    shell.enabled && hasHorizontalOverflow ? (
      <div
        ref={stickyScrollRef}
        className="h-3.5 flex-shrink-0 overflow-x-scroll scrollbar-always-visible border-t border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800"
        aria-label="Horizontal scroll"
      >
        <div style={{ width: Math.max(scrollWidth, 1), height: 14 }} />
      </div>
    ) : null;

  const showShellExpandControl = shell.enabled && shell.expandable && (shellOverflows || shellExpanded);
  const canClickToExpand = shell.expandable && !shellExpanded && shellOverflows;
  const shellHeightClassName = shell.expandable
    ? shellExpanded
      ? shell.heightClassName
      : shell.collapsedHeightClassName
    : shell.heightClassName;
  const shellYOverflowClass =
    shellHeightClassName != null
      ? shell.expandable && !shellExpanded
        ? 'overflow-y-hidden'
        : 'overflow-y-auto'
      : undefined;

  const toggleShellExpanded = (): void => {
    setShellExpanded((prev) => {
      const next = !prev;
      if (!next && scrollRef.current != null) {
        scrollRef.current.scrollTop = 0;
      }
      return next;
    });
  };

  const handleCollapsedTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!canClickToExpand) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    // Keep header filters, links, and other controls usable while collapsed.
    if (target.closest('button, a, input, select, textarea, [role="button"], [role="combobox"], [role="listbox"]')) {
      return;
    }
    toggleShellExpanded();
  };

  const shellExpandControl = showShellExpandControl ? (
    <div
      className={classnames(
        'flex flex-shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-3 py-1.5 dark:border-slate-700 dark:bg-slate-900',
        canClickToExpand && 'cursor-pointer'
      )}
      onClick={canClickToExpand ? toggleShellExpanded : undefined}
    >
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {shellExpanded
          ? 'Scrollable workspace'
          : `${recs.length} row${recs.length === 1 ? '' : 's'} · preview truncated`}
      </span>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
        aria-expanded={shellExpanded}
        onClick={(event) => {
          event.stopPropagation();
          toggleShellExpanded();
        }}
      >
        {shellExpanded ? (
          <>
            <ArrowsPointingInIcon className="h-3.5 w-3.5" aria-hidden />
            Collapse
          </>
        ) : (
          <>
            <ArrowsPointingOutIcon className="h-3.5 w-3.5" aria-hidden />
            Expand to scroll
          </>
        )}
      </button>
    </div>
  ) : null;

  const tableBody = shell.enabled ? (
    <div
      className={classnames(
        'flex min-w-0 flex-col overflow-hidden',
        shell.bordered && 'rounded-md border border-slate-200 dark:border-slate-700',
        canClickToExpand &&
          'cursor-pointer transition-[filter] duration-150 hover:brightness-[1.03] dark:hover:brightness-110'
      )}
    >
      <div className="relative min-h-0 min-w-0">
        <div
          ref={scrollRef}
          onClick={handleCollapsedTableClick}
          className={classnames(
            'overflow-x-scroll scrollbar-hidden',
            // Cap height on the scrollport (not the outer shell) so short tables shrink-wrap
            // instead of stretching to max-h via flex-1.
            shellYOverflowClass,
            shellHeightClassName,
            canClickToExpand && 'cursor-pointer'
          )}
        >
          {tableContent}
        </div>
        {canClickToExpand ? (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-12 bg-gradient-to-t from-white via-white/80 to-transparent dark:from-slate-800 dark:via-slate-800/80"
            aria-hidden
          />
        ) : null}
      </div>
      {shellExpandControl}
      {scrollTrack}
    </div>
  ) : noOverflow ? (
    tableContent
  ) : (
    <div className="overflow-x-auto">{tableContent}</div>
  );

  return (
    <div className="relative min-w-0">
      {loadingOverlay}
      {tableBody}
      {hidePaginator !== true && <Paginator data={rest} handlePaginatorClick={props.handlePaginatorClick} />}
    </div>
  )
}

export default Table;
